import React from 'react';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Compact code strip shown at top-right of voucher bodies.
 *
 * Render priority (Activity Voucher parity):
 *   1. `imageBase64` — exact pixel image cropped from the supplier's
 *      original voucher PDF. ALWAYS render this when present, regardless
 *      of pixel dimensions. Forced to a fixed display width of 120px
 *      (180px prominent) with `image-rendering: pixelated` so tiny source
 *      crops scale up sharply rather than blurring. The extracted image
 *      is the source of truth — never substitute a regenerated barcode.
 *   2. Regenerated code via react-barcode / qrcode.react — fallback ONLY
 *      when `imageBase64` is empty (legacy vouchers, manual-entry).
 *
 * Auto-renders a QR or a CODE128 barcode depending on `kind`:
 *   - "qr"      → QR code (modern airline / activity vouchers)
 *   - "barcode" → horizontal CODE128 barcode (legacy IndiGo, hotel chains)
 */
export default function VoucherBarcodeStrip({
  value,
  imageBase64,
  accent = '#595A5B',
  label = '',
  kind = 'barcode',
  size = 'compact', // 'compact' (right-rail) | 'prominent' (operational hero)
}) {
  if (!value && !imageBase64) return null;
  const safe = value ? String(value) : '';
  const isQr = (kind || '').toLowerCase() === 'qr';
  const prominent = size === 'prominent';
  const useOriginalImg = !!imageBase64;

  // Forced display width — pixelated upscaling preserves bar/QR edges even
  // when the source crop is 20px wide (typical Air India PDF coupon barcode).
  const imgDisplayWidth = prominent ? 180 : 120;

  const qrSize = prominent ? 144 : 68;
  const barcodeWidth = prominent ? 2.0 : 1.3;
  const barcodeHeight = prominent ? 64 : 32;
  const wrapperCls = prominent
    ? 'flex flex-col items-center'
    : 'flex flex-col items-end -mt-2';
  const codeBoxCls = prominent
    ? 'p-3 bg-white border border-slate-200 rounded-lg shadow-sm'
    : 'p-1 bg-white border border-slate-200 rounded-md';

  const originalImg = imageBase64 ? (
    <img
      src={imageBase64}
      alt={isQr ? 'Original voucher QR code' : 'Original voucher barcode'}
      data-testid="voucher-original-code-image"
      style={{
        display: 'block',
        width: `${imgDisplayWidth}px`,
        height: 'auto',
        imageRendering: 'pixelated',
      }}
    />
  ) : null;

  return (
    <div
      className={wrapperCls}
      data-testid="voucher-barcode-strip"
      data-code-kind={isQr ? 'qr' : 'barcode'}
      data-code-source={useOriginalImg ? 'original' : 'regenerated'}
    >
      <div className={codeBoxCls}>
        {useOriginalImg ? (
          originalImg
        ) : isQr ? (
          <QRCodeSVG value={safe} size={qrSize} level="M" fgColor="#111827" bgColor="#FFFFFF" />
        ) : (
          <Barcode
            value={safe}
            format="CODE128"
            width={barcodeWidth}
            height={barcodeHeight}
            displayValue={false}
            margin={0}
            background="#FFFFFF"
            lineColor="#111827"
          />
        )}
      </div>
      {/* Caption shown under the code.
          PROMINENT (top-of-voucher hero strip): show the actual decoded
          voucher number, NOT the literal word "Barcode" / "QR Code". If the
          payload is too long or unsafe to print, render blank.
          COMPACT (right-rail strip): keep the legacy "{label} · {value}"
          label since the small layout already has space for both.
          When `label` is omitted/empty: COMPACT renders nothing (caller wants
          a captionless strip); PROMINENT still renders the bare value. */}
      {(() => {
        if (!label) {
          if (prominent) {
            const display = safe.length > 0 ? safe : '';
            if (!display) return null;
            return (
              <div
                className="font-mono text-[10px] mt-2 uppercase break-all text-center max-w-[260px]"
                style={{ color: accent, letterSpacing: '0.18em' }}
                title={display}
                data-testid="voucher-code-caption"
              >
                {display}
              </div>
            );
          }
          return null;
        }
        if (prominent) {
          // Compact the payload so a 30-char Klook URL doesn't wrap
          // across multiple lines. Render blank when the value is missing.
          const display = safe.length > 0 ? safe : '';
          if (!display) return null;
          return (
            <div
              className="font-mono text-[10px] mt-2 uppercase break-all text-center max-w-[260px]"
              style={{ color: accent, letterSpacing: '0.18em' }}
              title={display}
              data-testid="voucher-code-caption"
            >
              {display}
            </div>
          );
        }
        return (
          <div
            className="font-mono text-[8px] mt-0.5 uppercase max-w-[180px] truncate"
            style={{ color: accent, letterSpacing: '0.18em' }}
            title={`${label} · ${safe}`}
            data-testid="voucher-code-caption"
          >
            {`${label} · ${safe}`}
          </div>
        );
      })()}
    </div>
  );
}

/**
 * Tiny QR shown alongside the barcode strip.
 * Useful when the public voucher URL is set on the voucher record.
 */
export function VoucherQRBadge({ value, size = 56 }) {
  if (!value) return null;
  return (
    <div className="p-1 bg-white border border-slate-200 rounded-md">
      <QRCodeSVG value={String(value)} size={size} level="M" fgColor="#111827" bgColor="#FFFFFF" />
    </div>
  );
}
